import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import {
  setConfigurationSections,
  setFormSections,
  setEditConfigurationData,
  setSelectedConfigurationId,
  setDrawerLoading,
  setTableLoading,
  setError,
  setPagination,
  setAllocationStats,
  setStatsLoading
} from './ciuAllocationSlice';
import { ConfigurationFormData } from './types';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';
import { mapApiResponseToFormData, mapFormDataToPayload } from './utils';
import { copyToClipboard } from '@cp/src/utils/commonUtils';

export const createCiuAllocationConfig =
  (formData: ConfigurationFormData, businessVertical: string, successCallback?: () => void) =>
  (dispatch: Dispatch) => {
    dispatch(setDrawerLoading(true));
    const payload = mapFormDataToPayload(formData);
    const url = getApiUrl(ApiKeys.ALLOCATION_CONFIGS_CREATE);
    axiosInstance
      .post(url, payload, {
        headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical }
      })
      .then(response => {
        toast.success('Configuration saved successfully');
        if (successCallback) {
          successCallback();
        }
        return response.data;
      })
      .catch(error => {
        logError(error);
        const errorMessage = error?.response?.data?.message || 'Failed to save configuration';
        toast.error(errorMessage);
      })
      .finally(() => {
        dispatch(setDrawerLoading(false));
      });
  };

export const getLatestCiuAllocationConfig = (businessVertical: string) => (dispatch: Dispatch) => {
  dispatch(setDrawerLoading(true));
  const url = getApiUrl(ApiKeys.ALLOCATION_CONFIGS_LATEST);
  axiosInstance
    .get(url, { headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical } })
    .then(response => {
      const sections = response?.data?.questionnaire?.sections || [];
      dispatch(setFormSections(sections));
    })
    .catch(error => {
      logError(error);
      const errorMessage = error?.response?.data?.message || 'Failed to fetch configuration';
      toast.error(errorMessage);
    })
    .finally(() => {
      dispatch(setDrawerLoading(false));
    });
};

export const getAllCiuAllocationConfigs =
  (page: number, size: number, businessVertical: string) => (dispatch: Dispatch) => {
    dispatch(setTableLoading(true));
    const url = getApiUrl(ApiKeys.ALLOCATION_CONFIGS_HISTORY);
    axiosInstance
      .get(url, {
        params: { page, size },
        headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical }
      })
      .then(response => {
        const sections = response?.data?.content || [];
        dispatch(setConfigurationSections(sections));
        dispatch(
          setPagination({
            pageIndex: response?.data?.number,
            pageSize: response?.data?.size,
            totalElements: response?.data?.totalElements
          })
        );
        dispatch(setTableLoading(false));
      })
      .catch(error => {
        logError(error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch configurations';
        toast.error(errorMessage);
        dispatch(setError(errorMessage));
        dispatch(setTableLoading(false));
      });
  };

export const getCiuAllocationConfigById =
  (configId: string, businessVertical: string, isDuplicate = false) =>
  (dispatch: Dispatch) => {
    dispatch(setDrawerLoading(true));
    if (!isDuplicate) {
      dispatch(setSelectedConfigurationId(configId));
    }
    const url = getApiUrl(ApiKeys.ALLOCATION_CONFIGS_BY_ID, { configId });
    axiosInstance
      .get(url, { headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical } })
      .then(response => {
        const sections = response?.data?.questionnaire?.sections || [];
        dispatch(setFormSections(sections));

        const mappedFormData = mapApiResponseToFormData(sections);
        dispatch(setEditConfigurationData(mappedFormData));
      })
      .catch(error => {
        logError(error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch configuration';
        toast.error(errorMessage);
        dispatch(setError(errorMessage));
      })
      .finally(() => {
        dispatch(setDrawerLoading(false));
      });
  };

export const deleteCiuAllocationConfigById =
  (configId: string, businessVertical: string, successCallback?: () => void) =>
  (dispatch: Dispatch) => {
    dispatch(setTableLoading(true));
    const url = getApiUrl(ApiKeys.ALLOCATION_CONFIGS_DELETE_CONFIG, { configId });
    axiosInstance
      .delete(url, { headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical } })
      .then(response => {
        if (response.status === API_STATUS_CODE.NO_CONTENT) {
          toast.success('Configuration deleted successfully');
          if (successCallback) {
            successCallback();
          }
        }
      })
      .catch(error => {
        toast.error('Failed to delete configuration');
        logError(error);
      })
      .finally(() => {
        dispatch(setTableLoading(false));
      });
  };

export const updateCiuAllocationConfigById =
  (
    configId: string,
    formData: ConfigurationFormData,
    businessVertical: string,
    successCallback?: () => void
  ) =>
  (dispatch: Dispatch) => {
    dispatch(setDrawerLoading(true));
    const payload = mapFormDataToPayload(formData);
    const url = getApiUrl(ApiKeys.ALLOCATION_CONFIGS_BY_ID, { configId });
    axiosInstance
      .put(url, payload, {
        headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical }
      })
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          toast.success('Configuration updated successfully');
          if (successCallback) {
            successCallback();
          }
        }
      })
      .catch(error => {
        logError(error);
        const errorMessage = error?.response?.data?.message || 'Failed to update configuration';
        toast.error(errorMessage);
      })
      .finally(() => {
        dispatch(setDrawerLoading(false));
      });
  };

export const getPreviewQuery =
  (formData: ConfigurationFormData, businessVertical: string) => (dispatch: Dispatch) => {
    dispatch(setDrawerLoading(true));
    const url = getApiUrl(ApiKeys.ALLOCATION_CONFIGS_PREVIEW_QUERY);
    const payload = mapFormDataToPayload(formData);
    axiosInstance
      .post(url, payload, {
        headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical }
      })
      .then(response => {
        const query = response?.data?.query || '';
        copyToClipboard(query);
        toast.success('Query copied to clipboard');
      })
      .catch(error => {
        logError(error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch preview query';
        toast.error(errorMessage);
      })
      .finally(() => {
        dispatch(setDrawerLoading(false));
      });
  };

export const getAllocationStats = (businessVertical: string) => (dispatch: Dispatch) => {
  dispatch(setStatsLoading(true));
  const url = getApiUrl(ApiKeys.GET_ALLOCATIONS_CONFIGS_STATS);
  axiosInstance
    .get(url, { headers: { donotHandleError: true, 'X-Business-Vertical': businessVertical } })
    .then(response => {
      const stats = response?.data;
      dispatch(setAllocationStats(stats));
    })
    .catch(error => {
      logError(error);
    })
    .finally(() => {
      dispatch(setStatsLoading(false));
    });
};
